<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { NotificationService } from '@/services/notification.services';
import type { Notification } from '@/services/notification.services';

const { t } = useI18n({ useScope: 'global' });

// ── Push config ───────────────────────────────────────────────────────────────
const pushEnabled = ref(false);
const permission = ref<NotificationPermission>('default');
async function requestPermission() {
  if (!('Notification' in window)) return;
  permission.value = await Notification.requestPermission();
  if (permission.value === 'granted') pushEnabled.value = true;
}
const toggles = computed(() => [
  {
    key: 'alerts',
    label: t('notifications.toggleAlerts'),
    sub: t('notifications.toggleAlertsSub'),
    enabled: true,
  },
  {
    key: 'orders',
    label: t('notifications.toggleOrders'),
    sub: t('notifications.toggleOrdersSub'),
    enabled: true,
  },
  {
    key: 'support',
    label: t('notifications.toggleSupport'),
    sub: t('notifications.toggleSupportSub'),
    enabled: false,
  },
  {
    key: 'activity',
    label: t('notifications.toggleActivity'),
    sub: t('notifications.toggleActivitySub'),
    enabled: false,
  },
]);

const queryClient = useQueryClient();

// ── Commerces ─────────────────────────────────────────────────────────────────
const selectedRestaurantId = ref<string | null>(null);

const { data: commercesData } = useQuery({
  queryKey: ['notif-commerces'],
  queryFn: async () => {
    const { data } = await axiosInstance.get('restaurants/search', {
      params: { pagination: true, pageSize: 100, sortBy: 'name', order: 'ASC' },
    });
    return data;
  },
});

const allCommerces = computed<any[]>(() => {
  const list =
    commercesData.value?.data ?? commercesData.value?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});

// ── Tabs ──────────────────────────────────────────────────────────────────────
const activeTab = ref<'all' | 'unread' | 'read'>('all');

const subjectLabels = computed<Record<string, string>>(() => ({
  order_rejected: t('notifications.subjectRejected'),
  order_aborded: t('notifications.subjectAborted'),
  order_accepted: t('notifications.subjectAccepted'),
  order_ready: t('notifications.subjectReady'),
  order_created: t('notifications.subjectCreated'),
  order_updated: t('notifications.subjectUpdated'),
  order_paid: t('notifications.subjectPaid'),
  order_delay: t('notifications.subjectDelay'),
}));

const subjectType = (s: string): 'error' | 'success' | 'warning' | 'info' => {
  if (['order_rejected', 'order_aborded'].includes(s)) return 'error';
  if (['order_accepted', 'order_ready', 'order_paid'].includes(s))
    return 'success';
  if (['order_delay', 'order_updated'].includes(s)) return 'warning';
  return 'info';
};

const notifKey = computed(() => [
  'notifications',
  activeTab.value,
  selectedRestaurantId.value,
]);

const { data, isPending, isError } = useQuery({
  queryKey: notifKey,
  queryFn: () =>
    NotificationService.search({
      pagination: true,
      pageSize: 50,
      status:
        activeTab.value === 'all'
          ? undefined
          : (activeTab.value as 'read' | 'unread'),
      restaurantId: selectedRestaurantId.value ?? undefined,
    }),
});

const notifications = computed<Notification[]>(() => {
  const items =
    (data.value as any)?.data ?? (data.value as any)?.notifications ?? [];
  return Array.isArray(items) ? items : [];
});

const unreadCount = computed(
  () => notifications.value.filter((n) => n.status === 'unread').length,
);

const { mutate: markRead } = useMutation({
  mutationFn: (id: string) => NotificationService.markAsRead(id),
  onSuccess: () =>
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
});

const { mutate: deleteNotif } = useMutation({
  mutationFn: (id: string) => NotificationService.delete(id),
  onSuccess: () =>
    queryClient.invalidateQueries({ queryKey: ['notifications'] }),
});

function markAllRead() {
  notifications.value
    .filter((n) => n.status === 'unread')
    .forEach((n) => markRead(n.id));
}

function formatDate(iso?: string) {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TYPE_CONFIG = {
  error: {
    border: '#ba1a1a',
    bg: 'rgba(186,26,26,0.08)',
    color: '#ba1a1a',
    icon: 'dangerous',
  },
  success: {
    border: '#10b981',
    bg: 'rgba(16,185,129,0.08)',
    color: '#059669',
    icon: 'check_circle',
  },
  warning: {
    border: '#f59e0b',
    bg: 'rgba(245,158,11,0.08)',
    color: '#d97706',
    icon: 'warning',
  },
  info: {
    border: '#3b82f6',
    bg: 'rgba(59,130,246,0.08)',
    color: '#2563eb',
    icon: 'system_update_alt',
  },
};
</script>

<template>
  <div class="np-layout">
    <!-- ══ COLONNE GAUCHE : notifications ══════════════════════════════════════ -->
    <div class="np-page">
      <!-- ── Header ── -->
      <div class="np-page__header">
        <div>
          <h2 class="np-page__title">{{ t('notifications.title') }}</h2>
          <p class="np-page__sub">{{ t('notifications.subtitle') }}</p>
        </div>
        <div class="np-page__header-actions">
          <div class="np-commerce-select">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <select v-model="selectedRestaurantId" class="np-select">
              <option :value="null">{{ t('notifications.allStores') }}</option>
              <option v-for="c in allCommerces" :key="c.id" :value="c.id">
                {{ c.designation ?? c.name }}
              </option>
            </select>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
          <button class="np-btn-outline" @click="markAllRead">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <polyline points="20 6 9 17 4 12" />
              <polyline points="20 12 9 23 4 18" />
            </svg>
            {{ t('notifications.markAllRead') }}
          </button>
        </div>
      </div>

      <!-- ── Tabs ── -->
      <div class="np-tabs">
        <button
          class="np-tab"
          :class="{ 'np-tab--active': activeTab === 'all' }"
          @click="activeTab = 'all'"
        >
          {{ t('notifications.tabAll') }}
          <span v-if="notifications.length" class="np-tab__count">{{
            notifications.length
          }}</span>
        </button>
        <button
          class="np-tab"
          :class="{ 'np-tab--active': activeTab === 'unread' }"
          @click="activeTab = 'unread'"
        >
          {{ t('notifications.tabUnread') }}
          <span
            v-if="unreadCount > 0"
            class="np-tab__count np-tab__count--red"
            >{{ unreadCount }}</span
          >
        </button>
        <button
          class="np-tab"
          :class="{ 'np-tab--active': activeTab === 'read' }"
          @click="activeTab = 'read'"
        >
          {{ t('notifications.tabRead') }}
        </button>
      </div>

      <!-- ── Loading ── -->
      <div v-if="isPending" class="np-feed">
        <div v-for="i in 4" :key="i" class="np-skeleton">
          <div class="np-skeleton__icon" />
          <div class="np-skeleton__body">
            <div class="np-skeleton__line np-skeleton__line--title" />
            <div class="np-skeleton__line np-skeleton__line--sub" />
            <div class="np-skeleton__line np-skeleton__line--short" />
          </div>
        </div>
      </div>

      <!-- ── Error ── -->
      <div v-else-if="isError" class="np-empty">
        <div class="np-empty__icon np-empty__icon--error">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <p class="np-empty__title">{{ t('notifications.errorTitle') }}</p>
        <p class="np-empty__sub">{{ t('notifications.errorSub') }}</p>
      </div>

      <!-- ── Empty ── -->
      <div v-else-if="notifications.length === 0" class="np-empty">
        <div class="np-empty__icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.2"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            <line x1="2" y1="2" x2="22" y2="22" stroke-width="1.5" />
          </svg>
        </div>
        <p class="np-empty__title">{{ t('notifications.emptyTitle') }}</p>
        <p class="np-empty__sub">{{ t('notifications.emptySub') }}</p>
      </div>

      <!-- ── Feed ── -->
      <div v-else class="np-feed">
        <div
          v-for="notif in notifications"
          :key="notif.id"
          class="np-card"
          :class="{ 'np-card--unread': notif.status === 'unread' }"
        >
          <div
            class="np-card__accent"
            :style="{
              background: TYPE_CONFIG[subjectType(notif.subject)].border,
            }"
          />
          <div
            class="np-card__icon"
            :style="{
              background: TYPE_CONFIG[subjectType(notif.subject)].bg,
              color: TYPE_CONFIG[subjectType(notif.subject)].color,
            }"
          >
            <svg
              v-if="subjectType(notif.subject) === 'success'"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4.5-4.5 1.41-1.41L10 13.67l7.09-7.09 1.41 1.41L10 16.5z"
              />
            </svg>
            <svg
              v-else-if="subjectType(notif.subject) === 'error'"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              />
            </svg>
            <svg
              v-else-if="subjectType(notif.subject) === 'warning'"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
            </svg>
            <svg
              v-else
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
              />
            </svg>
          </div>
          <div class="np-card__body">
            <div class="np-card__top">
              <div class="np-card__title-row">
                <h3 class="np-card__title">
                  {{ subjectLabels[notif.subject] ?? notif.subject }}
                </h3>
                <span
                  v-if="notif.status === 'unread'"
                  class="np-card__unread-dot"
                />
              </div>
              <span v-if="notif.createdAt" class="np-card__time">{{
                formatDate(notif.createdAt)
              }}</span>
            </div>
            <p v-if="notif.body" class="np-card__msg">{{ notif.body }}</p>
            <div class="np-card__actions">
              <button
                v-if="notif.status === 'unread'"
                class="np-action-btn np-action-btn--primary"
                @click.stop="markRead(notif.id)"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {{ t('notifications.markRead') }}
              </button>
              <button
                class="np-action-btn np-action-btn--ghost"
                @click.stop="deleteNotif(notif.id)"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
                  />
                </svg>
                {{ t('notifications.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ COLONNE DROITE : config push ════════════════════════════════════════ -->
    <aside class="np-push">
      <div class="np-push__header">
        <h2 class="np-push__title">{{ t('notifications.pushTitle') }}</h2>
        <p class="np-push__sub">{{ t('notifications.pushSub') }}</p>
      </div>

      <!-- Permission banner -->
      <div
        v-if="permission !== 'granted'"
        class="np-perm"
        :class="{ 'np-perm--denied': permission === 'denied' }"
      >
        <div class="np-perm__icon">
          <i :class="permission === 'denied' ? 'pi pi-ban' : 'pi pi-bell'" />
        </div>
        <div class="np-perm__text">
          <p class="np-perm__title">
            {{
              permission === 'denied'
                ? t('notifications.permDeniedTitle')
                : t('notifications.permTitle')
            }}
          </p>
          <p class="np-perm__sub">
            {{
              permission === 'denied'
                ? t('notifications.permDeniedSub')
                : t('notifications.permSub')
            }}
          </p>
        </div>
        <button
          v-if="permission !== 'denied'"
          class="np-perm__btn"
          @click="requestPermission"
        >
          {{ t('notifications.permAllow') }}
        </button>
      </div>

      <!-- Toggles -->
      <div class="np-toggle-list">
        <div v-for="item in toggles" :key="item.key" class="np-toggle-item">
          <div class="np-toggle-item__info">
            <p class="np-toggle-item__label">{{ item.label }}</p>
            <p class="np-toggle-item__sub">{{ item.sub }}</p>
          </div>
          <button
            class="np-toggle"
            :class="{ 'np-toggle--on': item.enabled }"
            role="switch"
            :aria-checked="item.enabled"
            @click="item.enabled = !item.enabled"
          >
            <span class="np-toggle__thumb" />
          </button>
        </div>
      </div>
    </aside>
  </div>
</template>

<style scoped>
/* ── Layout 2 colonnes ── */
.np-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: start;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .np-layout {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .np-layout {
    padding: 1rem;
    gap: 1rem;
  }
}
@media (min-width: 1024px) {
  .np-layout {
    grid-template-columns: 1fr 340px;
  }
}

/* ── Page (colonne gauche) ── */
.np-page {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

/* ── Header ── */
.np-page__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}
.np-page__title {
  font-size: 2rem;
  font-weight: 700;
  color: #141c29;
  margin: 0 0 0.35rem;
  letter-spacing: -0.01em;
}
.np-page__sub {
  font-size: 0.9375rem;
  color: #4d4632;
  margin: 0;
  opacity: 0.75;
}
.np-page__header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.np-commerce-select {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.875rem;
  height: 40px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 9999px;
  color: #4d4632;
  box-shadow: 0 2px 8px rgba(11, 19, 32, 0.04);
}
.np-select {
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  color: #141c29;
  outline: none;
  cursor: pointer;
  min-width: 0;
  max-width: 200px;
}

.np-btn-outline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.25rem;
  height: 40px;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 0.875rem;
  font-weight: 600;
  color: #141c29;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}
.np-btn-outline:hover {
  background: rgba(255, 255, 255, 0.7);
}

/* ── Tabs ── */
.np-tabs {
  display: flex;
  gap: 0.625rem;
  overflow-x: auto;
  scrollbar-width: none;
}
.np-tabs::-webkit-scrollbar {
  display: none;
}

.np-tab {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 0.875rem;
  font-weight: 600;
  color: #4d4632;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.2s,
    color 0.2s,
    box-shadow 0.2s;
}
.np-tab:hover {
  background: rgba(255, 255, 255, 0.65);
  color: #141c29;
}
.np-tab--active {
  background: var(--nxt-primary, #141c29);
  color: #fff;
  border-color: var(--nxt-primary, #141c29);
  box-shadow: 0 4px 15px rgba(20, 28, 41, 0.2);
}
.np-tab--active:hover {
  opacity: 0.88;
}

.np-tab__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.1);
  font-size: 0.6875rem;
  font-weight: 700;
}
.np-tab--active .np-tab__count {
  background: rgba(255, 255, 255, 0.25);
}
.np-tab__count--red {
  background: #ffdad6;
  color: #ba1a1a;
}
.np-tab--active .np-tab__count--red {
  background: rgba(186, 26, 26, 0.2);
  color: #ffdad6;
}

/* ── Feed ── */
.np-feed {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ── Skeleton ── */
.np-skeleton {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  animation: np-pulse 1.5s ease-in-out infinite;
}
.np-skeleton__icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: #e2e8f0;
  flex-shrink: 0;
}
.np-skeleton__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.np-skeleton__line {
  background: #e2e8f0;
  border-radius: 0.375rem;
}
.np-skeleton__line--title {
  height: 1.125rem;
  width: 55%;
}
.np-skeleton__line--sub {
  height: 0.875rem;
  width: 80%;
}
.np-skeleton__line--short {
  height: 0.875rem;
  width: 40%;
}
@keyframes np-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.55;
  }
}

/* ── Empty ── */
.np-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.875rem;
  padding: 5rem 2rem;
  text-align: center;
}
.np-empty__icon {
  width: 5rem;
  height: 5rem;
  border-radius: 1.5rem;
  background: #e7eeff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  margin-bottom: 0.5rem;
}
.np-empty__icon--error {
  background: #ffdad6;
  color: #ba1a1a;
}
.np-empty__title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
}
.np-empty__sub {
  font-size: 0.875rem;
  color: #4d4632;
  margin: 0;
  opacity: 0.7;
}

/* ── Card ── */
.np-card {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.5rem 1.5rem 1.5rem 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1rem;
  box-shadow: 0 20px 40px rgba(11, 19, 32, 0.02);
  position: relative;
  overflow: hidden;
  transition:
    transform 0.4s cubic-bezier(0.2, 1, 0.3, 1),
    box-shadow 0.4s cubic-bezier(0.2, 1, 0.3, 1);
}
.np-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(11, 19, 32, 0.07);
}
.np-card--unread {
  background: rgba(255, 255, 255, 0.88);
}

/* Left accent bar */
.np-card__accent {
  flex-shrink: 0;
  width: 6px;
  align-self: stretch;
  border-radius: 0 3px 3px 0;
  min-height: 100%;
  position: relative;
}

/* Icon */
.np-card__icon {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Body */
.np-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-right: 1rem;
}
.np-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.125rem;
}
.np-card__title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.np-card__title {
  font-size: 1rem;
  font-weight: 700;
  color: #141c29;
  margin: 0;
  line-height: 1.3;
}
.np-card__unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ba1a1a;
  flex-shrink: 0;
}
.np-card__time {
  font-size: 0.75rem;
  font-weight: 500;
  color: #4d4632;
  opacity: 0.6;
  white-space: nowrap;
  flex-shrink: 0;
}
.np-card__msg {
  font-size: 0.875rem;
  color: #4d4632;
  margin: 0;
  line-height: 1.6;
}

/* Actions (hidden, appear on hover) */
.np-card__actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.625rem;
  opacity: 0;
  transition: opacity 0.3s;
}
.np-card:hover .np-card__actions {
  opacity: 1;
}

.np-action-btn {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
}
.np-action-btn--primary {
  background: var(--nxt-primary, #141c29);
  color: #fff;
  border: none;
  box-shadow: 0 4px 12px rgba(20, 28, 41, 0.2);
}
.np-action-btn--primary:hover {
  opacity: 0.88;
  transform: translateY(-1px);
}
.np-action-btn--ghost {
  background: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #4d4632;
  backdrop-filter: blur(8px);
}
.np-action-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.7);
}

/* ── Push sidebar ── */
.np-push {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  position: sticky;
  top: 1rem;
}
.np-push__header {
}
.np-push__title {
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  font-weight: 800;
  color: #141c29;
}
.np-push__sub {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.np-perm {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.125rem;
  border-radius: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
}
.np-perm--denied {
  background: rgba(186, 26, 26, 0.04);
  border-color: rgba(186, 26, 26, 0.12);
}
.np-perm__icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #141c29;
  flex-shrink: 0;
}
.np-perm__text {
  flex: 1;
  min-width: 0;
}
.np-perm__title {
  margin: 0 0 0.2rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #141c29;
}
.np-perm__sub {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.4;
}
.np-perm__btn {
  margin-top: 0.25rem;
  padding: 0.375rem 0.875rem;
  border-radius: 0.5rem;
  border: none;
  background: var(--nxt-primary, #141c29);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}
.np-perm__btn:hover {
  opacity: 0.88;
}

.np-toggle-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 1.25rem;
  overflow: hidden;
}
.np-toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.125rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.np-toggle-item:last-child {
  border-bottom: none;
}
.np-toggle-item__info {
  flex: 1;
  min-width: 0;
}
.np-toggle-item__label {
  margin: 0 0 0.15rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #141c29;
}
.np-toggle-item__sub {
  margin: 0;
  font-size: 0.6875rem;
  color: #64748b;
  line-height: 1.4;
}

.np-toggle {
  width: 2.75rem;
  height: 1.625rem;
  min-width: 2.75rem;
  border-radius: 9999px;
  border: none;
  background: #e2e8f0;
  cursor: pointer;
  position: relative;
  transition: background 0.25s;
  padding: 0;
  flex-shrink: 0;
}
.np-toggle--on {
  background: var(--nxt-primary, #141c29);
}
.np-toggle__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  transition: transform 0.25s;
}
.np-toggle--on .np-toggle__thumb {
  transform: translateX(1.125rem);
}

/* Dark mode */
[data-theme='dark'] .np-page__title {
  color: #f1f5f9;
}
[data-theme='dark'] .np-page__sub {
  color: #94a3b8;
}
[data-theme='dark'] .np-commerce-select,
[data-theme='dark'] .np-btn-outline {
  background: rgba(30, 32, 36, 0.7);
  border-color: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
}
[data-theme='dark'] .np-select {
  color: #f1f5f9;
}
[data-theme='dark'] .np-tab {
  background: rgba(30, 32, 36, 0.6);
  border-color: rgba(255, 255, 255, 0.06);
  color: #94a3b8;
}
[data-theme='dark'] .np-tab--active {
  background: var(--nxt-primary, #141c29);
  color: #fff;
}
[data-theme='dark'] .np-card {
  background: rgba(30, 32, 36, 0.75);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .np-card--unread {
  background: rgba(30, 32, 36, 0.9);
}
[data-theme='dark'] .np-card__title {
  color: #f1f5f9;
}
[data-theme='dark'] .np-card__msg {
  color: #94a3b8;
}
[data-theme='dark'] .np-empty__title {
  color: #f1f5f9;
}
[data-theme='dark'] .np-skeleton {
  background: rgba(30, 32, 36, 0.6);
}
[data-theme='dark'] .np-skeleton__icon,
[data-theme='dark'] .np-skeleton__line {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .np-push {
  background: rgba(30, 32, 38, 0.8);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .np-push__title {
  color: #f1f5f9;
}
[data-theme='dark'] .np-toggle-list {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .np-toggle-item {
  border-color: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .np-toggle-item__label {
  color: #f1f5f9;
}
[data-theme='dark'] .np-toggle {
  background: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .np-toggle--on {
  background: #f1f5f9;
}
[data-theme='dark'] .np-toggle--on .np-toggle__thumb {
  background: #141c29;
}
</style>
